import * as THREE from 'three';

interface MorphableCubeParams {
  sizeX: number;
  sizeY: number;
  depth: number;
  trapezoidY: number; // Corresponds to original trapezoidFactorY
  trapezoidZ: number; // Corresponds to original trapezoidFactorZ
}

/**
 * Creates a BufferGeometry for a morphable cube with 24 distinct vertices.
 * THIS IS THE CORRECT, ORIGINAL LOGIC from useMorphParticleStore.
 * @param params - The parameters defining the cube's shape.
 * @returns A THREE.BufferGeometry representing the cube.
 */
export function createMorphableCubeGeometry(params: MorphableCubeParams): THREE.BufferGeometry {
  const { sizeX, sizeY, depth, trapezoidY, trapezoidZ } = params;

  // --- START: Exact logic from useMorphParticleStore ---
  const xFactor = 1.0 - trapezoidY; 
  const backScale = 1.0 - trapezoidZ; 

  const hx = sizeX / 2;
  const hy = sizeY / 2;
  const hz = depth / 2;

  const front = [
    new THREE.Vector3(-hx * xFactor,  hy, hz),
    new THREE.Vector3( hx * xFactor,  hy, hz),
    new THREE.Vector3( hx,           -hy, hz),
    new THREE.Vector3(-hx,           -hy, hz),
  ];
  const back = [
    new THREE.Vector3(front[0].x * backScale, front[0].y * backScale, -hz),
    new THREE.Vector3(front[1].x * backScale, front[1].y * backScale, -hz),
    new THREE.Vector3(front[2].x * backScale, front[2].y * backScale, -hz),
    new THREE.Vector3(front[3].x * backScale, front[3].y * backScale, -hz),
  ];

  const faces = [
      [front[0], front[3], front[2], front[1]], // Front
      [back[0], front[0], front[1], back[1]],   // Top
      [back[3], front[3], front[0], back[0]],   // Left
      [back[1], front[1], front[2], back[2]],   // Right
      [back[2], front[2], front[3], back[3]],   // Bottom
      [back[1], back[2], back[3], back[0]],   // Back
  ];
  // --- END: Exact logic from useMorphParticleStore ---

  const vertices = new Float32Array(24 * 3);
  for (let i = 0; i < faces.length; i++) {
    const face = faces[i];
    for (let j = 0; j < 4; j++) {
      const index = i * 4 + j;
      face[j].toArray(vertices, index * 3);
    }
  }

  const indices = new Uint16Array([
    // This order matches the 'getSolidIndices' function from the original store
    // (base, base + 1, base + 2, base, base + 2, base + 3)
    0, 1, 2,  0, 2, 3,    // Front
    4, 5, 6,  4, 6, 7,    // Top
    8, 9, 10, 8, 10, 11,  // Left
    12, 13, 14, 12, 14, 15, // Right
    16, 17, 18, 16, 18, 19, // Bottom
    20, 21, 22, 20, 22, 23, // Back
  ]);
  
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  
  geometry.computeVertexNormals(); 

  return geometry;
} 